import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Paper,
  Typography,
  FormControlLabel,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import WallpaperIcon from "@mui/icons-material/Wallpaper";
import SaveIcon from "@mui/icons-material/Save";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import * as actionRelease from "../../../containers/Releases/VideoReleases/actions";
import SelectCmp from "react-select";
import CustomShowModal from "../../../components/Common/CustomShowModal";

// Styled Components
const PageContainer = styled(Box)({
  minHeight: 'calc(100vh - 100px)',
  background: 'linear-gradient(135deg, #e8eef3 0%, #dce4ec 100%)',
  padding: '32px 24px',
});

const PageHeader = styled(Box)({
  marginBottom: '32px',
  '& h1': {
    fontSize: '2rem',
    fontWeight: 700,
    background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    marginBottom: '8px',
    fontFamily: "'DM Sans', sans-serif",
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  '& p': {
    color: '#5f6368',
    fontSize: '0.95rem',
    fontFamily: "'DM Sans', sans-serif",
  }
});

const StyledPaper = styled(Paper)({
  borderRadius: '16px',
  overflow: 'hidden',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  background: '#ffffff',
  marginBottom: '24px',
});

const SectionTitle = styled(Typography)({
  fontSize: '1.25rem',
  fontWeight: 600,
  color: '#202124',
  fontFamily: "'DM Sans', sans-serif",
  marginBottom: '20px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const StyledTextField = styled(TextField)({
  fontFamily: "'DM Sans', sans-serif",
  '& .MuiOutlinedInput-root': {
    background: '#ffffff',
    borderRadius: '8px',
    transition: 'all 0.3s ease',
    '& fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.12)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(26, 115, 232, 0.3)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#1a73e8',
      borderWidth: '2px',
    },
  },
  '& .MuiOutlinedInput-input': {
    color: '#202124',
    fontFamily: "'DM Sans', sans-serif",
    padding: '8.5px 14px',
  },
  '& .MuiInputLabel-root': {
    color: '#5f6368',
    fontFamily: "'DM Sans', sans-serif",
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#1a73e8',
  },
});

const ImageUploadContainer = styled(Box)({
  position: 'relative',
  width: '100%',
  aspectRatio: '16/9',
  borderRadius: '12px',
  overflow: 'hidden',
  border: '2px dashed #e0e0e0',
  backgroundColor: '#f8f9fa',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#1a73e8',
    backgroundColor: '#ffffff',
    boxShadow: '0 4px 12px rgba(26, 115, 232, 0.15)',
  },
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
});

const UploadPlaceholder = styled(Box)({
  textAlign: 'center',
  padding: '24px',
  '& .MuiSvgIcon-root': {
    fontSize: '3rem',
    color: '#1a73e8',
    marginBottom: '12px',
  },
  '& p': {
    color: '#5f6368',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.95rem',
    margin: '8px 0',
  },
  '& p:first-of-type': {
    fontWeight: 600,
    color: '#202124',
    fontSize: '1rem',
  },
});

const ChangeImageButton = styled(Button)({
  marginTop: '12px',
  fontFamily: "'DM Sans', sans-serif",
  textTransform: 'none',
  color: '#1a73e8',
  fontSize: '0.9rem',
  fontWeight: 600,
  '&:hover': {
    backgroundColor: 'rgba(26, 115, 232, 0.08)',
  },
});

const GuidelinesButton = styled(Button)({
  marginTop: '16px',
  width: '100%',
  fontFamily: "'DM Sans', sans-serif",
  textTransform: 'none',
  background: 'linear-gradient(135deg, #f093fb15 0%, #f5576c15 100%)',
  border: '2px solid #f5576c30',
  color: '#f5576c',
  fontSize: '0.9rem',
  fontWeight: 600,
  padding: '12px 24px',
  borderRadius: '8px',
  '&:hover': {
    background: 'linear-gradient(135deg, #f093fb25 0%, #f5576c25 100%)',
    borderColor: '#f5576c',
  },
});

const SubmitButton = styled(Button)({
  fontFamily: "'DM Sans', sans-serif",
  background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
  color: '#fff',
  padding: '12px 48px',
  borderRadius: '8px',
  textTransform: 'none',
  fontSize: '1rem',
  fontWeight: 600,
  boxShadow: '0 4px 12px rgba(26, 115, 232, 0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, #1557b0 0%, #009680 100%)',
    boxShadow: '0 6px 20px rgba(26, 115, 232, 0.4)',
  },
  '&:disabled': {
    background: '#e0e0e0',
    color: '#9e9e9e',
  },
});

const PlatformCard = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  padding: '16px',
  borderRadius: '12px',
  backgroundColor: '#f8f9fa',
  border: '2px solid #e0e0e0',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#ffffff',
    borderColor: '#1a73e8',
    boxShadow: '0 4px 12px rgba(26, 115, 232, 0.1)',
  },
  '& img': {
    height: '40px',
    objectFit: 'contain',
    marginRight: '12px',
  },
  '& h4': {
    margin: 0,
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#202124',
  },
});

const InfoBox = styled(Box)({
  backgroundColor: '#e3f2fd',
  border: '1px solid #1a73e8',
  borderRadius: '8px',
  padding: '12px 16px',
  marginTop: '8px',
  marginBottom: '16px',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
  '& .MuiSvgIcon-root': {
    color: '#1a73e8',
    fontSize: '1.2rem',
    marginTop: '2px',
  },
  '& p': {
    margin: 0,
    fontSize: '0.85rem',
    color: '#202124',
    fontFamily: "'DM Sans', sans-serif",
    lineHeight: 1.5,
    '& a': {
      color: '#1a73e8',
      textDecoration: 'none',
      fontWeight: 600,
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
});

const TermsBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  backgroundColor: '#f8f9fa',
  borderRadius: '12px',
  border: '2px solid #e0e0e0',
  '& .MuiFormControlLabel-label': {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.95rem',
    color: '#202124',
    '& a': {
      color: '#1a73e8',
      textDecoration: 'none',
      fontWeight: 600,
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
});

const selectStyles = {
  control: (base, state) => ({
    ...base,
    background: '#ffffff',
    borderRadius: '8px',
    borderColor: state.isFocused ? '#1a73e8' : 'rgba(0, 0, 0, 0.12)',
    borderWidth: state.isFocused ? '2px' : '1px',
    boxShadow: 'none',
    '&:hover': {
      borderColor: 'rgba(26, 115, 232, 0.3)',
    },
  }),
  singleValue: (base) => ({
    ...base,
    color: '#202124',
    fontFamily: "'DM Sans', sans-serif"
  }),
  placeholder: (base) => ({
    ...base,
    color: '#5f6368',
    fontFamily: "'DM Sans', sans-serif"
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#1a73e8' : state.isFocused ? 'rgba(26, 115, 232, 0.08)' : '#ffffff',
    color: state.isSelected ? '#ffffff' : '#202124',
    fontFamily: "'DM Sans', sans-serif",
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(26, 115, 232, 0.08)',
    },
  }),
  input: (base) => ({
    ...base,
    color: '#202124',
  }),
};

const FieldLabel = styled(Typography)({
  fontWeight: 600,
  fontSize: '0.95rem',
  color: '#202124',
  marginBottom: '8px',
  fontFamily: "'DM Sans', sans-serif",
  '& span': {
    color: '#f44336',
    marginLeft: '4px',
  },
});

function CreateVideoRelease(props) {
  const {
    recipeFilePreview,
    onFileHandler,
    setVReleaseData,
    vReleaseData,
    selectedSong,
    setselectedSong,
    selectedRelease,
    setSelectedRelease,
    checkTerm,
    setCheckTerm,
    onSubmitVRelease,
  } = props;

  const [open, setOpen] = useState(false);
  const [releases, setReleases] = useState([]);
  const [songs, setSongs] = useState([]);
  const [platformLogo, setPlatformLogo] = useState([]);
  const [loader, setLoader] = useState(false);
  const [logoLoader, setLogoLoader] = useState(false);

  useEffect(() => {
    onFetchLogos();
    getInitialData();
    setTimeout(() => {
      setLoader(true);
    }, 4000);
  }, []);

  useEffect(() => {
    onGetSongsByRelease();
  }, [selectedRelease]);

  const getInitialData = async () => {
    try {
      setLoader(true);
      const response = await actionRelease.getReleaseTitlesForVRelease();
      setReleases(
        response.body.map((item) => ({
          ...item,
          label: item.release_title,
          value: item.release_id,
        }))
      );
      setLoader(false);
      setSelectedRelease(null);
    } catch (ex) {
      setLoader(false);
    }
  };

  const onGetSongsByRelease = async () => {
    try {
      if (!selectedRelease?.release_id) return;
      setSongs([]);
      setselectedSong(null);
      setLoader(true);
      const { body: songs = [] } =
        (await actionRelease.getAudioTitlesByReleaseId(
          selectedRelease.release_id
        )) || {};
      setSongs(
        songs.map((item) => ({
          ...item,
          label: item.title,
          value: item.audio_id,
        }))
      );
      setLoader(false);
    } catch (ex) {
      setLoader(false);
    }
  };

  const onFetchLogos = () => {
    setLogoLoader(true);
    actionRelease
      .getLogos()
      .then((res) => {
        setLogoLoader(false);
        setPlatformLogo(res.body);
      })
      .catch(() => {
        setLogoLoader(false);
      });
  };

  let videoPlatformLogo = platformLogo.filter((item) => item.type === "3");

  return (
    <PageContainer>
      <PageHeader>
        <Typography component="h1">
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              position: 'relative',
              '& .MuiSvgIcon-root': {
                background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }
            }}
          >
            <VideoLibraryIcon sx={{ fontSize: 40, color: '#1a73e8' }} />
          </Box>
          Create Video Release
        </Typography>


        <Typography>
          Distribute your music videos to major platforms worldwide
        </Typography>
      </PageHeader>

      {/* Video Thumbnail Upload Section */}
      <StyledPaper>
        <Box p={3}>
          <SectionTitle>
            <WallpaperIcon />
            Video Thumbnail
          </SectionTitle>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <ImageUploadContainer>
                {recipeFilePreview ? (
                  <img src={recipeFilePreview} alt="Video Thumbnail" />
                ) : (
                  <label htmlFor="releaseInfo-file-upload" style={{ cursor: 'pointer', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <UploadPlaceholder>
                      <CloudUploadIcon />
                      <p>Upload Video Thumbnail</p>
                      <p>Recommended Size: 1920px x 1080px</p>
                      <p style={{ fontSize: '0.85rem', color: '#9e9e9e' }}>PNG, JPG or JPEG (Max 1.5MB)</p>
                    </UploadPlaceholder>
                  </label>
                )}
              </ImageUploadContainer>
              {recipeFilePreview && (
                <label htmlFor="releaseInfo-file-upload">
                  <ChangeImageButton component="span">
                    Change Thumbnail
                  </ChangeImageButton>
                </label>
              )}
              <input
                type="file"
                id="releaseInfo-file-upload"
                accept="image/x-png,image/gif,image/jpeg"
                onChange={onFileHandler}
                style={{ display: 'none' }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" flexDirection="column" height="100%" justifyContent="center">
                <Typography variant="h6" fontWeight={600} color="#202124" fontFamily="'DM Sans', sans-serif" mb={2}>
                  Thumbnail Guidelines
                </Typography>
                <Typography variant="body2" color="#5f6368" fontFamily="'DM Sans', sans-serif" mb={1}>
                  • Use high-quality images in 16:9 aspect ratio
                </Typography>
                <Typography variant="body2" color="#5f6368" fontFamily="'DM Sans', sans-serif" mb={1}>
                  • Avoid text overlays or release dates
                </Typography>
                <Typography variant="body2" color="#5f6368" fontFamily="'DM Sans', sans-serif" mb={1}>
                  • Keep file size under 1.5MB
                </Typography>
                <Typography variant="body2" color="#5f6368" fontFamily="'DM Sans', sans-serif" mb={2}>
                  • Ensure clarity at various sizes
                </Typography>
                <GuidelinesButton
                  startIcon={<InfoOutlinedIcon />}
                  onClick={() => setOpen(true)}
                >
                  View Complete Video Guidelines
                </GuidelinesButton>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </StyledPaper>

      {/* Release & Audio Selection */}
      <StyledPaper>
        <Box p={3}>
          <SectionTitle>
            <InfoOutlinedIcon />
            Video Details
          </SectionTitle>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FieldLabel>
                Select Release<span>*</span>
              </FieldLabel>
              <SelectCmp
                styles={selectStyles}
                options={releases}
                value={selectedRelease}
                placeholder="Choose a release"
                onChange={setSelectedRelease}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FieldLabel>
                Select Audio<span>*</span>
              </FieldLabel>
              <SelectCmp
                styles={selectStyles}
                options={songs}
                value={selectedSong}
                placeholder="Choose an audio track"
                onChange={setselectedSong}
                isDisabled={!selectedRelease}
              />
            </Grid>

            <Grid item xs={12}>
              <FieldLabel>
                Download Link to Video<span>*</span>
              </FieldLabel>
              <StyledTextField
                variant="outlined"
                fullWidth
                size="small"
                placeholder="https://wetransfer.com/your-video-link"
                value={vReleaseData.download_link || ''}
                onChange={(e) =>
                  setVReleaseData({
                    ...vReleaseData,
                    download_link: e.target.value,
                  })
                }
              />
              <InfoBox>
                <InfoOutlinedIcon />
                <p>
                  Preferred sites: <a href="https://wetransfer.com/" target="_blank" rel="noreferrer">WeTransfer</a>, <a href="https://www.dropbox.com/" target="_blank" rel="noreferrer">Dropbox</a>, <a href="https://drive.google.com/" target="_blank" rel="noreferrer">Google Drive</a>, <a href="https://fromsmash.com/" target="_blank" rel="noreferrer">FromSmash</a>
                </p>
              </InfoBox>
            </Grid>

            <Grid item xs={12} md={6}>
              <FieldLabel>
                Release Date<span>*</span>
              </FieldLabel>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  format="DD/MM/YYYY"
                  minDate={moment().add(6, 'd').startOf('day')}
                  value={vReleaseData.release_date ? moment(vReleaseData.release_date) : null}
                  onChange={(date) =>
                    setVReleaseData({
                      ...vReleaseData,
                      release_date: (date && date.valueOf()) || null,
                    })
                  }
                  enableAccessibleFieldDOMStructure={false}
                  slots={{
                    textField: (params) => (
                      <StyledTextField
                        {...params}
                        size="small"
                        fullWidth
                        placeholder="Select release date"
                        inputProps={{
                          ...params.inputProps,
                        }}
                        InputProps={{
                          ...params.InputProps,
                        }}
                      />
                    )
                  }}
                  slotProps={{
                    openPickerIcon: {
                      sx: {
                        color: '#1a73e8',
                      }
                    },
                    popper: {
                      sx: {
                        '& .MuiPaper-root': {
                          borderRadius: '12px',
                          boxShadow: '0 12px 32px rgba(0, 0, 0, 0.3)',
                          background: '#e8eef3',
                          border: '1px solid rgba(0, 0, 0, 0.2)',
                        },
                        '& .MuiPickersDay-root': {
                          color: '#202124',
                          fontWeight: 500,
                          '&:hover': {
                            background: 'rgba(26, 115, 232, 0.2)',
                          },
                          '&.Mui-selected': {
                            background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
                            color: '#ffffff',
                            fontWeight: 600,
                            '&:hover': {
                              background: 'linear-gradient(135deg, #1557b0 0%, #009688 100%)',
                            }
                          },
                          '&.Mui-disabled': {
                            color: '#ef5350 !important',
                            fontWeight: 500,
                            textDecoration: 'line-through',
                            opacity: 1,
                          }
                        },
                        '& .MuiPickersDay-root.Mui-disabled': {
                          color: '#ef5350 !important',
                          fontWeight: 600,
                          textDecoration: 'line-through',
                        },
                        '& button.MuiPickersDay-root.Mui-disabled': {
                          color: '#ef5350 !important',
                          fontWeight: 600,
                        },
                        '& .MuiPickersDay-root[disabled]': {
                          color: '#ef5350 !important',
                          fontWeight: 600,
                        },
                        '& .MuiDayCalendar-weekDayLabel.Mui-disabled': {
                          color: '#ef5350 !important',
                        },
                        '& .MuiPickersDay-dayOutsideMonth': {
                          color: '#ef5350 !important',
                        },
                        '& .MuiPickersYear-yearButton': {
                          color: '#202124',
                          fontWeight: 500,
                          fontSize: '1rem',
                          '&:hover': {
                            background: 'rgba(26, 115, 232, 0.2)',
                          },
                          '&.Mui-selected': {
                            background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
                            color: '#ffffff',
                            fontWeight: 600,
                            '&:hover': {
                              background: 'linear-gradient(135deg, #1557b0 0%, #009688 100%)',
                            }
                          },
                        },
                        '& .MuiYearCalendar-root': {
                          color: '#202124',
                        },
                        '& .MuiPickersCalendarHeader-root': {
                          color: '#202124',
                          background: 'linear-gradient(135deg, rgba(26, 115, 232, 0.15) 0%, rgba(0, 191, 165, 0.15) 100%)',
                          padding: '16px',
                          marginBottom: '8px',
                        },
                        '& .MuiPickersCalendarHeader-label': {
                          color: '#202124',
                          fontWeight: 700,
                          fontSize: '1rem',
                        },
                        '& .MuiPickersArrowSwitcher-button': {
                          color: '#1a73e8',
                          '&:hover': {
                            background: 'rgba(26, 115, 232, 0.15)',
                          }
                        },
                        '& .MuiDayCalendar-weekDayLabel': {
                          color: '#5f6368',
                          fontWeight: 700,
                          fontSize: '0.875rem',
                        },
                        '& .MuiDayCalendar-header': {
                          background: 'transparent',
                          paddingTop: '8px',
                        },
                        '& .MuiPickersLayout-root': {
                          background: '#e8eef3',
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} md={6}>
              <FieldLabel>Original Release Date</FieldLabel>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  format="DD/MM/YYYY"
                  value={vReleaseData.ogn_release_date ? moment(vReleaseData.ogn_release_date) : null}
                  onChange={(date) =>
                    setVReleaseData({
                      ...vReleaseData,
                      ogn_release_date: (date && date.valueOf()) || null,
                    })
                  }
                  enableAccessibleFieldDOMStructure={false}
                  slots={{
                    textField: (params) => (
                      <StyledTextField
                        {...params}
                        size="small"
                        fullWidth
                        placeholder="Select original release date"
                        inputProps={{
                          ...params.inputProps,
                        }}
                        InputProps={{
                          ...params.InputProps,
                        }}
                      />
                    )
                  }}
                  slotProps={{
                    openPickerIcon: {
                      sx: {
                        color: '#1a73e8',
                      }
                    },
                    popper: {
                      sx: {
                        '& .MuiPaper-root': {
                          borderRadius: '12px',
                          boxShadow: '0 12px 32px rgba(0, 0, 0, 0.3)',
                          background: '#e8eef3',
                          border: '1px solid rgba(0, 0, 0, 0.2)',
                        },
                        '& .MuiPickersDay-root': {
                          color: '#202124',
                          fontWeight: 500,
                          '&:hover': {
                            background: 'rgba(26, 115, 232, 0.2)',
                          },
                          '&.Mui-selected': {
                            background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
                            color: '#ffffff',
                            fontWeight: 600,
                            '&:hover': {
                              background: 'linear-gradient(135deg, #1557b0 0%, #009688 100%)',
                            }
                          }
                        },
                        '& .MuiPickersYear-yearButton': {
                          color: '#202124',
                          fontWeight: 500,
                          fontSize: '1rem',
                          '&:hover': {
                            background: 'rgba(26, 115, 232, 0.2)',
                          },
                          '&.Mui-selected': {
                            background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
                            color: '#ffffff',
                            fontWeight: 600,
                            '&:hover': {
                              background: 'linear-gradient(135deg, #1557b0 0%, #009688 100%)',
                            }
                          },
                        },
                        '& .MuiYearCalendar-root': {
                          color: '#202124',
                        },
                        '& .MuiPickersCalendarHeader-root': {
                          color: '#202124',
                          background: 'linear-gradient(135deg, rgba(26, 115, 232, 0.15) 0%, rgba(0, 191, 165, 0.15) 100%)',
                          padding: '16px',
                          marginBottom: '8px',
                        },
                        '& .MuiPickersCalendarHeader-label': {
                          color: '#202124',
                          fontWeight: 700,
                          fontSize: '1rem',
                        },
                        '& .MuiPickersArrowSwitcher-button': {
                          color: '#1a73e8',
                          '&:hover': {
                            background: 'rgba(26, 115, 232, 0.15)',
                          }
                        },
                        '& .MuiDayCalendar-weekDayLabel': {
                          color: '#5f6368',
                          fontWeight: 700,
                          fontSize: '0.875rem',
                        },
                        '& .MuiDayCalendar-header': {
                          background: 'transparent',
                          paddingTop: '8px',
                        },
                        '& .MuiPickersLayout-root': {
                          background: '#e8eef3',
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} md={6}>
              <FieldLabel>Video UPC</FieldLabel>
              <StyledTextField
                variant="outlined"
                fullWidth
                size="small"
                placeholder="Enter UPC if available (numbers only)"
                value={vReleaseData.video_upc || ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  setVReleaseData({
                    ...vReleaseData,
                    video_upc: value,
                  });
                }}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*'
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FieldLabel>Video ISRC</FieldLabel>
              <StyledTextField
                variant="outlined"
                fullWidth
                size="small"
                placeholder="Enter ISRC if available"
                value={vReleaseData.video_isrc || ''}
                onChange={(e) =>
                  setVReleaseData({
                    ...vReleaseData,
                    video_isrc: e.target.value,
                  })
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FieldLabel>Actor Names</FieldLabel>
              <StyledTextField
                variant="outlined"
                fullWidth
                size="small"
                placeholder="Separate names with commas"
                value={vReleaseData.actor || ''}
                onChange={(e) =>
                  setVReleaseData({
                    ...vReleaseData,
                    actor: e.target.value,
                  })
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FieldLabel>Actress Names</FieldLabel>
              <StyledTextField
                variant="outlined"
                fullWidth
                size="small"
                placeholder="Separate names with commas"
                value={vReleaseData.actress || ''}
                onChange={(e) =>
                  setVReleaseData({
                    ...vReleaseData,
                    actress: e.target.value,
                  })
                }
              />
            </Grid>
          </Grid>
        </Box>
      </StyledPaper>

      {/* Video Platforms Section */}
      <StyledPaper>
        <Box p={3}>
          <SectionTitle>
            <VideoLibraryIcon />
            Distribution Platforms (30+)
          </SectionTitle>
          {logoLoader ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={40} sx={{ color: '#1a73e8' }} />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {videoPlatformLogo.map((item, key) => (
                <Grid key={key} item xs={12} sm={6} md={4}>
                  <PlatformCard>
                    <img src={item.logo} alt={item.name} />
                    <h4>{item.name}</h4>
                    <Checkbox
                      checked
                      size="medium"
                      sx={{
                        marginLeft: 'auto',
                        color: '#1a73e8',
                        '&.Mui-checked': {
                          color: '#1a73e8',
                        },
                      }}
                    />
                  </PlatformCard>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </StyledPaper>

      {/* Terms and Submit */}
      <StyledPaper>
        <Box p={3}>
          <TermsBox>
            <FormControlLabel
              control={
                <Checkbox
                  checked={checkTerm}
                  onChange={(e) => setCheckTerm(e.target.checked)}
                  sx={{
                    color: '#1a73e8',
                    '&.Mui-checked': {
                      color: '#1a73e8',
                    },
                  }}
                />
              }
              label={
                <span>
                  I understand and agree to the{" "}
                  <a
                    href="https://fronicmedia.com/distribution-agreement"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Fronic Media Distribution Terms
                  </a>{" "}
                  &{" "}
                  <a
                    href="http://fronicmedia.com/privacy-policy"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Privacy Policy
                  </a>
                </span>
              }
            />
          </TermsBox>

          <Box display="flex" justifyContent="center" mt={3}>
            <SubmitButton
              onClick={onSubmitVRelease}
              startIcon={<SaveIcon />}
              disabled={!checkTerm}
            >
              Submit Video Release
            </SubmitButton>
          </Box>
        </Box>
      </StyledPaper>

      {/* Guidelines Modal */}
      <CustomShowModal
        open={open}
        handleClose={() => setOpen(false)}
        maxWidth="md"
        title="MUSIC VIDEO GUIDELINES/SPECIFICATIONS"
      >
        <Box p={2}>
          <Typography variant="h6" fontWeight={700} mb={2} fontFamily="'DM Sans', sans-serif">
            Music Video Guidelines
          </Typography>
          <Typography variant="body2" mb={2} fontFamily="'DM Sans', sans-serif" lineHeight={1.8}>
            <strong>Graphic Overlays:</strong> Music videos must not contain chyrons, static or scrolling lyrics, or lower-third graphics.
            <br /><br />
            <strong>Release Dates or Advertisements:</strong> Music videos must not contain release dates, logos, or advertisements.
            <br /><br />
            <strong>Nonstandard Music Videos:</strong> Music videos that are artist interviews, commentaries, behind the scenes, or similar in content must be bundled with a music album and cannot be sold individually.
            <br /><br />
            <strong>Promotional Still Image Videos:</strong> Promotional videos (such as videos displaying only the cover art and audio or slide shows) will not be accepted.
            <br /><br />
            <strong>Teasers, Trailers and Partial Videos:</strong> Music videos which have been shortened or edited into a promotional teaser, trailer or partial version will not be accepted.
            <br /><br />
            <strong>Music Video Quality:</strong> Music videos with poor quality (such as glitches, blurriness, incomplete video or audio, no audio, out of sync audio and video, and so on) will not be accepted.
            <br /><br />
            <strong>Explicit Content:</strong> Music videos that contain explicit language, nudity, drug references, or depictions of drug use must be marked "Explicit."
            <br /><br />
            <strong>Music Videos from Films:</strong> Music videos taken from musical films must contain a title version that refers to the film such as (From "Name of Film").
          </Typography>

          <Typography variant="h6" fontWeight={700} mt={3} mb={2} fontFamily="'DM Sans', sans-serif">
            Music Video Specifications
          </Typography>
          <Box component="ul" sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', lineHeight: 2 }}>
            <li><strong>File Format:</strong> QuickTime (.mov files)</li>
            <li><strong>Codec:</strong> Apple PRO RES 422 (HQ)</li>
            <li><strong>Size:</strong> 1920×1080 pixels</li>
            <li><strong>Video Bitrate:</strong> ~220 Mb/s</li>
            <li><strong>Framerate:</strong> 24 or 25 fps</li>
            <li><strong>Colorspace:</strong> YUV 422</li>
            <li><strong>Gamma Range:</strong> Between 2.15 and 2.25</li>
            <li><strong>Pixel ratio:</strong> Ratio must be 1:1 (square pixel)</li>
            <li><strong>Progressive:</strong> The video must not be interlaced</li>
            <li><strong>Black frame beginning/ending:</strong> The video must contain one black frame at the beginning and at the end</li>
            <li><strong>Sound Quality:</strong> Codec – PCM Sampling frequency, 48 kHz Sample size, 16 bits Audio bitrate, ~1536 kb/s, 2 Channels (stereo)</li>
          </Box>
        </Box>
      </CustomShowModal>
    </PageContainer>
  );
}

export default CreateVideoRelease;
